// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/Thread.test.tsx
// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import { Button, Flex, Text } from "@radix-ui/themes";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CommentContextChip } from "./CommentContextChip";
import { type AskV2ThreadMessage } from "./types";
import { type AskV2TransportApi } from "./useAskV2Transport";

import { AskPunttThread } from "../../components/ask-puntt-thread";

type FeedMember = {
  _id: string;
  name?: string | null;
  email?: string | null;
  avatar?: string | null;
};

type ThreadProps = {
  transport: AskV2TransportApi;
  presentation?: "chat" | "feed";
  feedActorName?: string;
  feedActorAvatar?: string;
  feedMembers?: FeedMember[];
  currentUserId?: string;
  /**
   * Read-only viewer. Three affordances change here, and this list is the
   * whole of it — each is a write the page's gated transport cannot swallow
   * safely, and each is enforced by a separate term below:
   *
   * 1. `chatId` is withheld from `AskPunttThread`, so `ChatMessage` draws no
   *    per-row thumbs. Feedback POSTs its own mutation, never transport.
   * 2. `onSubmitAnswers` is withheld, so the agent's question form draws no
   *    live Submit. A no-op handler would still draw one.
   * 3. The failed-row Retry button is dropped. The failure notice stays.
   *
   * All three are refusals, not silent no-ops: the composer lock already
   * refuses the send, so an affordance that looks live and does nothing is
   * the worse outcome. The per-site comments below carry the reasoning for
   * each term; this is the one place that names all three together.
   */
  readOnly?: boolean;
};

function transportMessageKey(message: AskV2ThreadMessage): string {
  return message.serverId ?? message.clientId;
}

export function AskV2Thread({
  transport,
  presentation = "chat",
  feedActorName,
  feedActorAvatar,
  feedMembers,
  currentUserId,
  readOnly = false,
}: ThreadProps) {
  const { t } = useTranslation("ui");

  const normalizedFeedMembers = useMemo(
    () =>
      feedMembers?.map((member) => ({
        _id: member._id,
        name: member.name ?? undefined,
        email: member.email ?? undefined,
        avatar: member.avatar,
      })),
    [feedMembers],
  );

  const { send } = transport;
  // A form the agent drew is answered as the next message, in words. Pass the
  // formatted answer straight to send() so it never flashes in the composer.
  // Read-only viewers are refused here — the backend would 403 the send.
  const submitAnswers = useCallback(
    (formattedAnswers: string) => {
      if (readOnly) return;
      void send({ message: formattedAnswers });
    },
    [send, readOnly],
  );

  // ChatMessage takes `chatId` for exactly one thing: the per-row thumbs —
  // the `useSubmitAskPunttFeedback` mutation, the `chatId` term in its
  // `showFeedback` guard, and that handler's analytics are its only readers.
  // Feedback never touches AskV2TransportApi, so the page's gated transport
  // cannot cover it, and `AskPunttThreadProps` declares no read-only flag to
  // forward — passing one is a compile error, not a dropped signal. So the
  // gate rides the props that component DOES declare, and the feedback half
  // of it is derived here, in one place, so this reasoning cannot drift away
  // from the `readOnly` term that enforces it: no chatId, no thumbs, so the
  // 403 the composer lock exists to avoid can never be fired from a row.
  const threadChatId = readOnly ? undefined : (transport.chatId ?? undefined);

  const transportById = useMemo(() => {
    const map = new Map<string, AskV2ThreadMessage>();
    for (const message of transport.messages) {
      map.set(transportMessageKey(message), message);
      map.set(message.clientId, message);
    }
    return map;
  }, [transport.messages]);

  if (
    transport.displayMessages.length === 0 &&
    transport.messages.length === 0
  ) {
    return null;
  }

  const failedById = new Map(
    transport.messages
      .filter((message) => message.status === "failed")
      .map((message) => [transportMessageKey(message), message]),
  );

  return (
    <div data-testid="ask-v2-thread" data-presentation={presentation}>
      <AskPunttThread
        messages={transport.displayMessages}
        presentation={presentation}
        chatId={threadChatId}
        waitingForAI={transport.inFlight}
        feedActorName={feedActorName}
        feedActorAvatar={feedActorAvatar}
        feedMembers={normalizedFeedMembers}
        currentUserId={currentUserId}
        // Withheld, not merely no-op'd, for a read-only viewer: AskPunttThread
        // derives `isLastAssistant` — and ChatAtoms its `interactive` — from
        // whether a submit handler exists at all, so handing it `undefined` is
        // what keeps the agent's question form from DRAWING a live Submit for
        // a viewer whose answer the backend would 403. The early return in
        // `submitAnswers` stays as defence in depth for any other call site.
        onSubmitAnswers={readOnly ? undefined : submitAnswers}
        // why: `transport.inFlight` is the very field `send()` refuses on, so a
        // form the agent drew mid-turn is locked by the same signal that would
        // drop its answer. Anything else re-opens the silent-drop gap.
        formDisabled={transport.inFlight}
        hideAgentChromeForMessage={(message) => {
          const transportMessage = transportById.get(message._id);
          return (
            transportMessage?.status === "aborted" &&
            message.role === "assistant"
          );
        }}
        renderBeforeMessage={(message) => {
          const hasQuoted = (message.quotedContexts?.length ?? 0) > 0;
          const hasComment = (message.commentContexts?.length ?? 0) > 0;
          if (message.role !== "user" || (!hasQuoted && !hasComment)) {
            return null;
          }
          return (
            <Flex gap="2" wrap="wrap" justify="end">
              {message.commentContexts?.map((ctx) => (
                <CommentContextChip
                  key={ctx.id}
                  context={ctx}
                  testId={`ask-v2-sent-comment-context-${message._id}-${ctx.id}`}
                />
              ))}
              {message.quotedContexts?.map((ctx) => (
                <Flex
                  key={ctx.id}
                  align="center"
                  className="max-w-full rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                  data-testid={`ask-v2-sent-quoted-context-${message._id}-${ctx.id}`}
                >
                  <Text
                    size="1"
                    className="max-w-[200px] truncate text-puntt-neutral-gray-11"
                  >
                    {ctx.text}
                  </Text>
                </Flex>
              ))}
            </Flex>
          );
        }}
        renderAfterMessage={(message) => {
          const failed = failedById.get(message._id);
          if (failed) {
            return (
              <div className="mt-2 flex items-center gap-2">
                <Text
                  as="p"
                  size="1"
                  color="red"
                  data-testid={`ask-v2-send-failed-${failed.clientId}`}
                >
                  {t("askV2.transport.failed")}
                </Text>
                {/* Retry is a write: it re-POSTs the turn through
                    transport.retry, which the backend 403s for this viewer. A
                    read-only viewer CAN reach a failed row — AskV2ChatPage's
                    `canInteract` fails OPEN while history loads, so a send in
                    that window leaves a failed bubble that outlives the lock.
                    The page's gated transport then swallows the click in
                    silence, leaving a button that looks live and never does
                    anything. Drop the affordance and keep the failure notice,
                    matching the locked composer and the answer form above. */}
                {readOnly ? null : (
                  <Button
                    size="1"
                    variant="ghost"
                    data-testid={`ask-v2-retry-${failed.clientId}`}
                    onClick={() => {
                      void transport.retry(failed.clientId);
                    }}
                  >
                    {t("askV2.transport.retry")}
                  </Button>
                )}
              </div>
            );
          }

          const transportMessage = transportById.get(message._id);
          if (transportMessage?.status === "sending") {
            return (
              <Text
                as="p"
                size="1"
                color="gray"
                data-testid={`ask-v2-message-sending-${transportMessageKey(transportMessage)}`}
              >
                {t("askV2.transport.sending")}
              </Text>
            );
          }
          if (transportMessage?.status === "aborted") {
            return (
              <Text as="p" size="1" color="gray">
                {t("askV2.transport.stopped")}
              </Text>
            );
          }
          return null;
        }}
      />
    </div>
  );
}
